// BKT Mastery Tracker — Per-skill mastery using Bayesian Knowledge Tracing
// Multi-cert: all mastery state is cert-scoped via certPath()
// Cache is keyed by [certId][userId][skillId]

import { certPath, read, write } from './firebase-store.js';
import {
  BKT_P_L0, BKT_P_T, BKT_P_G, BKT_P_S,
  MASTERY_INTENSIVE, MASTERY_SPACED
} from './constants.js';

// In-memory cache: { [certId]: { [userId]: { [skillId]: { pL, attempts, lastUpdated } } } }
const _cache = {};

/**
 * Load skill mastery state from Firebase (or cache).
 * @param {string} certId
 * @param {string} userId
 * @param {string} skillId
 */
async function _loadSkill(certId, userId, skillId) {
  if (!_cache[certId]) _cache[certId] = {};
  if (!_cache[certId][userId]) _cache[certId][userId] = {};
  if (_cache[certId][userId][skillId]) return _cache[certId][userId][skillId];

  let data = null;
  try {
    data = await read(certPath(userId, certId, 'mastery/' + skillId));
  } catch (e) {
    // Firebase not initialized — use in-memory only
  }
  const skill = data || { pL: BKT_P_L0, attempts: 0, lastUpdated: null };
  _cache[certId][userId][skillId] = skill;
  return skill;
}

/**
 * Save skill mastery state to Firebase and cache (cert-scoped).
 * @param {string} certId
 * @param {string} userId
 * @param {string} skillId
 * @param {object} skill
 */
async function _saveSkill(certId, userId, skillId, skill) {
  if (!_cache[certId]) _cache[certId] = {};
  if (!_cache[certId][userId]) _cache[certId][userId] = {};
  _cache[certId][userId][skillId] = skill;
  try {
    await write(certPath(userId, certId, 'mastery/' + skillId), skill);

    // Append to P(L) history for BKT validation (timestamped trail)
    const historyPath = certPath(userId, certId, 'mastery/' + skillId + '/history');
    const existing = await read(historyPath) || [];
    existing.push({ timestamp: Date.now(), pL: Math.round(skill.pL * 1000) / 1000 });
    // Cap at 100 entries to prevent unbounded growth
    if (existing.length > 100) existing.splice(0, existing.length - 100);
    await write(historyPath, existing);
  } catch (e) {
    // Firebase not initialized — in-memory only
  }
}

/**
 * BKT posterior update.
 */
function bktUpdate(pL, correct) {
  pL = Math.max(0.001, Math.min(0.999, Number.isFinite(pL) ? pL : BKT_P_L0));

  let pLGivenObs;
  if (correct) {
    const num = pL * (1 - BKT_P_S);
    const denom = num + (1 - pL) * BKT_P_G;
    pLGivenObs = denom > 0 ? num / denom : pL;
  } else {
    const num = pL * BKT_P_S;
    const denom = num + (1 - pL) * (1 - BKT_P_G);
    pLGivenObs = denom > 0 ? num / denom : pL;
  }

  const pLNew = pLGivenObs + (1 - pLGivenObs) * BKT_P_T;
  return Math.max(0.01, Math.min(0.99, pLNew));
}

/**
 * Classify mastery level from P(L).
 */
function classifyMastery(pL) {
  if (pL >= MASTERY_SPACED) return 'mastered';
  if (pL >= MASTERY_INTENSIVE) return 'review';
  return 'learning';
}

/**
 * Update mastery for all skills tagged on a question (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {Array<string>} skills - skill tags from the question
 * @param {boolean} correct
 * @returns {object} masteryDeltas - { [skillId]: { before, after, level } }
 */
export async function updateMastery(userId, certId, skills, correct) {
  if (!skills || skills.length === 0) return {};

  const deltas = {};

  for (const skillId of skills) {
    const skill = await _loadSkill(certId, userId, skillId);
    const before = skill.pL;

    skill.pL = bktUpdate(before, correct);
    skill.attempts = (skill.attempts || 0) + 1;
    skill.lastUpdated = new Date().toISOString();

    await _saveSkill(certId, userId, skillId, skill);

    deltas[skillId] = {
      before: Math.round(before * 1000) / 1000,
      after: Math.round(skill.pL * 1000) / 1000,
      level: classifyMastery(skill.pL)
    };
  }

  return deltas;
}

/**
 * Get all skill mastery probabilities for a user (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @returns {{ [skillId]: { pL, level, attempts } }}
 */
export async function getSkillMastery(userId, certId) {
  const data = await read(certPath(userId, certId, 'mastery')) || {};
  const result = {};

  for (const [skillId, skill] of Object.entries(data)) {
    result[skillId] = {
      pL: skill.pL,
      level: classifyMastery(skill.pL),
      attempts: skill.attempts || 0
    };
  }

  return result;
}

/**
 * Get domain mastery as weighted average of child skill masteries (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {number} domain
 * @returns {object|null} { pL, level, skillCount }
 */
export async function getDomainMastery(userId, certId, domain) {
  const allSkills = await read(certPath(userId, certId, 'mastery')) || {};
  const domainSkills = Object.entries(allSkills).filter(([id]) =>
    id.startsWith('d' + domain + '-') || id.startsWith(domain + '.')
  );

  if (domainSkills.length === 0) return null;

  const avgPL = domainSkills.reduce((sum, [, s]) => sum + (s.pL || BKT_P_L0), 0) / domainSkills.length;

  return {
    pL: Math.round(avgPL * 1000) / 1000,
    level: classifyMastery(avgPL),
    skillCount: domainSkills.length
  };
}

// Export BKT math for testing
export { bktUpdate, classifyMastery };
